/**
 * useMessageOperations - 消息操作 Hook
 * 提取自 ChatPage.tsx，包含发送、重试、编辑等消息操作
 *
 * 主要功能：
 * 1. handleSendMessage - 发送新消息
 * 2. handleRetry - 重试消息
 * 3. handleEditSave - 编辑并重新发送消息
 * 4. handleNewConversation - 创建新对话
 */

import { useCallback, useRef, useEffect } from 'react';
import {
  Conversation,
  MessageStatus,
  Message,
  MessageBlock,
  Choices,
  GroupChatMessage,
} from '@/types';
import { generateId, truncateText } from '@/lib/utils';
import { useUIStore } from '@/stores';
import { rebuildMessagesWithVersionPath } from '@/services/conversationService';
import { sendGroupMessage } from '@/services/groupChatService';
import socketService from '@/lib/socket';
// apiPost removed - skill tasks use fetch directly to avoid axios JSON parsing on SSE streams
import { createPendingId } from '@/utils/messageUtils';
import { useSkillTaskStore } from '@/stores/skillTaskStore';

export interface UseMessageOperationsOptions {
  // 对话相关
  currentConversationId: string | undefined;
  currentConversation: Conversation | undefined;
  conversationsRef: React.MutableRefObject<Conversation[]>;
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  setCurrentConversationId: (id: string | undefined) => void;

  // Sidebar 缓存
  setSidebarProjectConversationsCache: (
    updater: (
      prev: Record<string, Conversation[]>
    ) => Record<string, Conversation[]>
  ) => void;

  // 消息发送
  sendMessage: (
    content: string,
    conversationId: string,
    pendingId: string,
    projectId: string,
    agentId?: string | null,
    options?: any,
    lastMessageId?: number
  ) => Promise<void>;

  // 状态更新
  updateConversationLoadingState: (
    conversationId: string,
    isLoading: boolean
  ) => void;

  // Agent 相关
  selectedAgent?: { id: string; name: string; logo?: string } | null;
  newChatAgent?: { id: string; name: string; logo?: string } | null;
  setConversationAgent: (conversationId: string, agent: any) => void;
  setNewChatAgent: (agent: any) => void;

  // 项目相关
  projectId: string | null;

  // Refs
  currentConversationIdRef: React.MutableRefObject<string | undefined>;
  currentConversationIdRefForCallback: React.MutableRefObject<
    string | undefined
  >;
}

export function useMessageOperations({
  currentConversationId,
  conversationsRef,
  setConversations,
  setCurrentConversationId,
  setSidebarProjectConversationsCache,
  sendMessage,
  updateConversationLoadingState,
  selectedAgent,
  newChatAgent,
  setConversationAgent,
  setNewChatAgent,
  projectId,
  currentConversationIdRef,
  currentConversationIdRefForCallback,
}: UseMessageOperationsOptions) {
  // Refs for stable function references
  const sendMessageRef = useRef(sendMessage);
  const setConversationsRef = useRef(setConversations);
  const setSidebarProjectConversationsCacheRef = useRef(
    setSidebarProjectConversationsCache
  );
  const updateConversationLoadingStateRef = useRef(
    updateConversationLoadingState
  );
  const setCurrentConversationIdRef = useRef(setCurrentConversationId);

  // Update refs
  sendMessageRef.current = sendMessage;
  setConversationsRef.current = setConversations;
  setSidebarProjectConversationsCacheRef.current =
    setSidebarProjectConversationsCache;
  updateConversationLoadingStateRef.current = updateConversationLoadingState;
  setCurrentConversationIdRef.current = setCurrentConversationId;

  // 🔥 新增：监听 clearWaitingNodes 事件，删除已渲染的 waiting 节点
  // 当 HTTP 请求级别错误发生时，需要清除已经渲染的 waiting 状态节点
  useEffect(() => {
    const handleClearWaitingNodes = (event: Event) => {
      const { conversationId } = (event as CustomEvent).detail;
      if (!conversationId) return;

      console.log('🗑️ 清除 waiting 节点', { conversationId });

      setConversationsRef.current(prev =>
        prev.map(conv => {
          if (conv.id !== conversationId || !conv.treeData) return conv;

          // 递归删除所有 messageStatus 为 'waiting' 的节点
          const removeWaitingNodes = (nodes: any[]): any[] => {
            return nodes
              .filter(node => node.messageStatus !== 'waiting')
              .map(node => {
                if (node.children && node.children.length > 0) {
                  return {
                    ...node,
                    children: removeWaitingNodes(node.children),
                  };
                }
                return node;
              });
          };

          return {
            ...conv,
            treeData: removeWaitingNodes(conv.treeData),
          };
        })
      );
    };

    window.addEventListener('clearWaitingNodes', handleClearWaitingNodes);
    return () => {
      window.removeEventListener('clearWaitingNodes', handleClearWaitingNodes);
    };
  }, []);

  // 🔥 显式注册 socketService.on('chat_message', ...) 监听 WebSocket 事件
  // 用于 skill 会话：从 WS 接收流式数据（self_message / message_init / content / think / hash-code）
  useEffect(() => {
    // 🔥 调试：监听所有 chat_message 事件，无论是否是 skill 会话
    const handleSkillChatMessage = (rawData: string | GroupChatMessage) => {
      // 🔥 第 1 层日志：原始数据（确认是否收到了任何 WS 消息
      let data: GroupChatMessage;
      try {
        data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      } catch (e) {
        console.error('[Skill WS] Failed to parse chat_message:', e);
        return;
      }

      // 🔥 第 2 层日志：解析后的数据
      const convId = data.conversationId ? String(data.conversationId) : '';
      const isSkill = convId
        ? useUIStore.getState().isSkillConversation(convId)
        : false;
      const allSkillConvs = useUIStore.getState().activeSkillConversations;
      // console.log('[Skill WS] 🔍 Parsed:', {
      //   type: data.type,
      //   convId,
      //   messageId: data.messageId,
      //   isSkillConversation: isSkill,
      //   activeSkillConversations: Object.keys(allSkillConvs),
      // });

      // 🔥 只处理属于 skill 会话的消息
      if (!convId) return;

      // 🔥 如果真实 convId 不在 skill 会话中，检查是否有 temp ID 的 skill 会话需要迁移
      // SSE 可能在 message_init 之前就结束了，导致 convertTempToRealId 没被调用
      if (!isSkill) {
        const tempSkillIds = Object.keys(allSkillConvs).filter(id =>
          id.startsWith('temp-')
        );
        // 🔥 修复：只在恰好一个 temp skill 对话时才迁移
        // 多个 temp 对话同时存在时无法确定哪个是目标，跳过迁移避免串台
        if (tempSkillIds.length === 1) {
          // 找到唯一的 temp ID skill 会话，迁移到真实 ID
          const tempId = tempSkillIds[0];
          console.log('[Skill WS] 🔄 Migrating skill conversation:', {
            from: tempId,
            to: convId,
          });
          useUIStore.getState().removeSkillConversation(tempId);
          useUIStore.getState().addSkillConversation(convId);

          // 🔥 同时更新 conversation 的 ID 映射（如果还是 temp ID）
          setConversationsRef.current(prev => {
            const tempConv = prev.find(c => c.id === tempId);
            if (tempConv) {
              const converted = {
                ...tempConv,
                id: convId,
                conversationId: convId,
              };
              return prev
                .filter(c => c.id !== tempId && c.id !== convId)
                .concat(converted);
            }
            return prev;
          });

          // 🔥 关键修复：同时更新 currentConversationId（否则页面找不到 conversation 跳到欢迎页）
          if (
            currentConversationIdRef.current === tempId ||
            currentConversationIdRefForCallback.current === tempId
          ) {
            console.log('[Skill WS] 🔄 Updating currentConversationId:', {
              from: tempId,
              to: convId,
            });
            setCurrentConversationIdRef.current(convId);
            currentConversationIdRef.current = convId;
            currentConversationIdRefForCallback.current = convId;
          }
        } else {
          return; // 确实不是 skill 会话，跳过
        }
      }

      switch (data.type) {
        case 'self_message': {
          const blocks: MessageBlock[] = [];
          if (data.fileIds && data.fileIds.length > 0) {
            data.fileIds.forEach(fileId => {
              blocks.push({
                type: 'file',
                content: String(fileId),
                id: String(fileId),
                fileId: String(fileId),
              });
            });
          }
          if (data.content)
            blocks.push({ type: 'text', content: data.content });
          if (blocks.length === 0) blocks.push({ type: 'text', content: '' });
          const choice: Choices = { blocks, version: 1 };
          const selfMsg: Message = {
            id: String(data.messageId || Date.now()),
            backendId: data.messageId,
            previousMessageId: data.previousMessageId,
            role: 'user',
            choices: [choice],
            timestamp: new Date(),
            isStreaming: false,
          };
          (selfMsg as any).user = data.user;
          (selfMsg as any).isOwnMessage = true;
          (selfMsg as any).client = data.client;
          updateConversationLoadingStateRef.current(convId, true);
          setConversationsRef.current(prev =>
            prev.map(conv => {
              if (conv.id !== convId && conv.conversationId !== convId)
                return conv;
              const msgs = [...(conv.messages || [])];
              // 🔥 Skill 模式：查找最后一个没有 backendId 的 user 消息（占位消息）并更新，避免重复
              let placeholderIdx = -1;
              for (let i = msgs.length - 1; i >= 0; i--) {
                if (msgs[i].role === 'user' && !msgs[i].backendId) {
                  placeholderIdx = i;
                  break;
                }
              }
              if (placeholderIdx >= 0) {
                msgs[placeholderIdx] = {
                  ...msgs[placeholderIdx],
                  ...selfMsg,
                  id: selfMsg.id,
                };
                return { ...conv, messages: msgs };
              }
              // 🔥 Skill 模式：检查是否已存在相同 backendId 的消息，避免重复
              const alreadyExistsByBackendId =
                selfMsg.backendId &&
                msgs.some(
                  m => m.role === 'user' && m.backendId === selfMsg.backendId
                );
              if (alreadyExistsByBackendId) {
                console.log(
                  '[Skill WS] self_message: message already exists by backendId, skipping',
                  { backendId: selfMsg.backendId }
                );
                return conv;
              }
              return { ...conv, messages: [...msgs, selfMsg] };
            })
          );
          break;
        }
        case 'message_init': {
          const emptyChoice: Choices = { blocks: [], version: 1 };
          const assistantMsg: Message = {
            id: String(data.messageId || Date.now()),
            backendId: data.messageId,
            previousMessageId: data.previousMessageId,
            completionId: data.completionId,
            role: 'assistant',
            choices: [emptyChoice],
            timestamp: new Date(),
            isStreaming: true,
          };
          // 🔥 设置 client 信息：优先用 WS 事件中的，回退到 uiStore 中的 selectedAgent
          // 后端可能返回 client 对象，也可能返回 clientId + clientName 独立字段
          if (data.client) {
            assistantMsg.client = {
              id: String(data.client.id),
              name: data.client.name,
              logo: data.client.logo,
            };
          } else if (data.clientId) {
            // 🔥 根据 clientId 从 store 的 agents 列表中查找对应的 agent，获取 logo
            const matchedAgent = useUIStore
              .getState()
              .agents.find(a => String(a.id) === String(data.clientId));
            assistantMsg.client = {
              id: String(data.clientId),
              name:
                data.clientName || matchedAgent?.name || String(data.clientId),
              logo: matchedAgent?.logo,
            };
          } else {
            // WS 事件没带 client，从 uiStore 获取当前 agent
            const { selectedAgent: storeAgent, conversationAgentMap } =
              useUIStore.getState();
            const convAgent = convId ? conversationAgentMap[convId] : null;
            const fallbackAgent = convAgent || storeAgent;

            if (fallbackAgent) {
              assistantMsg.client = {
                id: fallbackAgent.id,
                name: fallbackAgent.name,
                logo: fallbackAgent.logo,
              };
            }
          }
          setConversationsRef.current(prev =>
            prev.map(conv => {
              if (conv.id !== convId && conv.conversationId !== convId)
                return conv;
              const msgs = [...(conv.messages || [])];
              // 🔥 Skill 模式：查找最后一个没有 backendId 且正在 streaming 的 assistant 消息（占位消息）
              let existingAssistantIdx = -1;
              for (let i = msgs.length - 1; i >= 0; i--) {
                if (
                  msgs[i].role === 'assistant' &&
                  msgs[i].isStreaming &&
                  !msgs[i].backendId
                ) {
                  existingAssistantIdx = i;
                  break;
                }
              }
              if (existingAssistantIdx >= 0) {
                // 更新占位消息：设置 backendId、client 等后端信息
                msgs[existingAssistantIdx] = {
                  ...msgs[existingAssistantIdx],
                  id: assistantMsg.id,
                  backendId: assistantMsg.backendId,
                  previousMessageId: assistantMsg.previousMessageId,
                  completionId: assistantMsg.completionId,
                  client: assistantMsg.client,
                  isStreaming: true,
                };
                return { ...conv, messages: msgs };
              }
              // 🔥 Skill 模式：检查是否已存在相同 backendId 的消息，避免重复
              const assistantAlreadyExists =
                assistantMsg.backendId &&
                msgs.some(
                  m =>
                    m.role === 'assistant' &&
                    m.backendId === assistantMsg.backendId
                );
              if (assistantAlreadyExists) {
                console.log(
                  '[Skill WS] message_init: assistant already exists by backendId, skipping',
                  { backendId: assistantMsg.backendId }
                );
                return conv;
              }
              // 没有找到占位消息，正常添加
              return {
                ...conv,
                messages: [...msgs, assistantMsg],
              };
            })
          );

          // 🔥 不再在 message_init 时调用 fetchBoard
          // board 数据由 WS board_step_* 事件实时构建（updateBoardStep 会自动创建 board）
          // fetchBoard 仅在切换到已有 skill 对话时调用（加载历史数据）

          // 🔥 关键修复：同步更新 latestMessageInitMap，确保下次发送消息时
          // getLastAssistantMessageId() 能返回 WS 推送的最新 assistantMessageId
          // 而不是使用旧的/过期的 ID（SSE 流在 skill 模式下可能提前结束，导致 setLatestMessageInit 未被调用）
          if (data.messageId) {
            useUIStore.getState().setLatestMessageInit({
              conversationId: convId,
              userMessageId: data.previousMessageId || 0,
              assistantMessageId: data.messageId,
              pendingId: '', // WS 推送的消息没有 pendingId
            });
            console.log('[Skill WS] 📍 setLatestMessageInit 同步完成', {
              convId,
              userMessageId: data.previousMessageId,
              assistantMessageId: data.messageId,
            });
          }

          break;
        }
        case 'content':
        case 'text': {
          if (!data.content) break;
          const textMsgId = data.messageId;
          setConversationsRef.current(prev =>
            prev.map(conv => {
              if (conv.id !== convId && conv.conversationId !== convId)
                return conv;
              const msgs = [...(conv.messages || [])];
              for (let i = msgs.length - 1; i >= 0; i--) {
                // 🔥 根据 messageId 匹配目标 assistant 消息，而非固定追加到最后一个
                if (
                  msgs[i].role === 'assistant' &&
                  (!textMsgId ||
                    msgs[i].backendId === textMsgId ||
                    String(msgs[i].id) === String(textMsgId))
                ) {
                  const msg = msgs[i];
                  const ac = msg.activeChoice || 0;
                  const cur = msg.choices[ac];
                  if (!cur) break;
                  const blks = [...cur.blocks];
                  const last = blks[blks.length - 1];
                  if (last && last.type === 'text') {
                    blks[blks.length - 1] = {
                      ...last,
                      content: last.content + data.content,
                    };
                  } else {
                    blks.push({ type: 'text', content: data.content || '' });
                  }
                  const uc: Choices = { ...cur, blocks: blks };
                  const ucs = [...msg.choices];
                  ucs[ac] = uc;
                  msgs[i] = { ...msg, choices: ucs };
                  break;
                }
              }
              return { ...conv, messages: msgs };
            })
          );
          break;
        }
        case 'think': {
          if (!data.content) break;
          const thinkMsgId = data.messageId;
          setConversationsRef.current(prev =>
            prev.map(conv => {
              if (conv.id !== convId && conv.conversationId !== convId)
                return conv;
              const msgs = [...(conv.messages || [])];
              for (let i = msgs.length - 1; i >= 0; i--) {
                // 🔥 根据 messageId 匹配目标 assistant 消息
                if (
                  msgs[i].role === 'assistant' &&
                  (!thinkMsgId ||
                    msgs[i].backendId === thinkMsgId ||
                    String(msgs[i].id) === String(thinkMsgId))
                ) {
                  const msg = msgs[i];
                  const ac = msg.activeChoice || 0;
                  const cur = msg.choices[ac];
                  if (!cur) break;
                  const blks = [...cur.blocks];
                  const last = blks[blks.length - 1];
                  if (last && last.type === 'think') {
                    blks[blks.length - 1] = {
                      ...last,
                      content: last.content + data.content,
                    };
                  } else {
                    blks.push({ type: 'think', content: data.content || '' });
                  }
                  const uc: Choices = { ...cur, blocks: blks };
                  const ucs = [...msg.choices];
                  ucs[ac] = uc;
                  msgs[i] = { ...msg, choices: ucs };
                  break;
                }
              }
              return { ...conv, messages: msgs };
            })
          );
          break;
        }
        case 'hash_code':
        case 'hash-code': {
          console.log('[Skill WS] AI response completed:', {
            convId,
            messageId: data.messageId,
          });
          const hashMsgId = data.messageId;
          setConversationsRef.current(prev =>
            prev.map(conv => {
              if (conv.id !== convId && conv.conversationId !== convId)
                return conv;
              const msgs = (conv.messages || []).map(msg => {
                // 🔥 根据 messageId 匹配目标 assistant 消息，精确结束 streaming
                if (
                  msg.role === 'assistant' &&
                  msg.isStreaming &&
                  (!hashMsgId ||
                    msg.backendId === hashMsgId ||
                    String(msg.id) === String(hashMsgId))
                )
                  return { ...msg, isStreaming: false };
                return msg;
              });
              return { ...conv, messages: msgs, isLoading: false };
            })
          );
          // 🔥 不移除 skill 会话标记：整个 conversation 进入 skill 模式后永久保持
          // uiState.removeSkillConversation(convId);  // 已移除
          // 只清理任务队列
          const uiState = useUIStore.getState();
          const { taskQueue, removeTask } = uiState;
          taskQueue
            .filter(
              t =>
                t.conversationId === convId &&
                (t.status === 'sent' ||
                  t.status === 'initialized' ||
                  t.status === 'streaming')
            )
            .forEach(t => removeTask(t.id));
          // 刷新侧边栏
          window.refreshSidebarRecent?.();
          window.refreshSidebarStarred?.();
          break;
        }
        case 'board_step_start':
        case 'board_step_end':
        case 'board_step_error':
        case 'board_step_status':
        case 'board_state': {
          // 🔥 处理 board 相关事件
          // board_state socket 通道的事件 data.type 实际是 board_step_start / board_step_end / board_step_error
          const boardData = data as any;
          console.log('[Skill WS] board event received:', {
            type: boardData.type,
            convId,
            index: boardData.index,
            step: boardData.step,
            name: boardData.name,
            fullData: JSON.stringify(boardData).substring(0, 500),
          });

          // 🔥 使用 updateBoardStep 更新 board 步骤状态
          // 后端可能用 step 或 index 字段传递步骤序号
          const stepIndex = boardData.index ?? boardData.step ?? 0;
          useSkillTaskStore.getState().updateBoardStep({
            type: boardData.type,
            conversationId: convId,
            index: stepIndex,
            name: boardData.name,
            description: boardData.description,
            startedAt: boardData.startedAt,
            finishedAt: boardData.finishedAt,
            error: boardData.error,
            stepCount: boardData.stepCount, // 🔥 后端返回的总步骤数
            status: boardData.status, // 🔥 board 整体状态（running/waiting/done）
          });

          // 🔥 当 board_state 的 status 为 waiting 或 board_step_end 事件时，刷新 Project Files 列表
          // 后端在这些阶段可能已生成新文件，需要同步到前端文件树
          if (
            (boardData.type === 'board_state' &&
              boardData.status === 'waiting') ||
            boardData.type === 'board_step_end'
          ) {
            console.log(
              '[Skill WS] board event triggers file refresh:',
              { type: boardData.type, status: boardData.status },
              '-> dispatching refreshProjectFiles event'
            );
            window.dispatchEvent(
              new CustomEvent('refreshProjectFiles', {
                detail: { conversationId: convId },
              })
            );
          }

          // 🔥 只在 board_step_start 且当前对话匹配时打开 Tasks Status 面板
          // 避免用户切换到 new chat 后，后台 skill 任务的事件意外重新打开 dashboard
          if (boardData.type === 'board_step_start') {
            const currentConvId = currentConversationIdRef.current;
            if (currentConvId && currentConvId === convId) {
              console.log(
                '[Skill WS] board_step_start -> opening dashboard (current conv)'
              );
              useSkillTaskStore.getState().setDashboardOpen(true);
            } else {
              console.log(
                '[Skill WS] board_step_start -> skipping dashboard open (not current conv)',
                { convId, currentConvId }
              );
            }
          }
          break;
        }

        default: {
          console.log('[Skill WS] Unhandled message type:', data.type);
        }
      }
    };

    // 🔥 显式注册 WebSocket 监听器
    socketService.on('chat_message', handleSkillChatMessage);
    socketService.on('board_state', handleSkillChatMessage);

    return () => {
      socketService.off('chat_message', handleSkillChatMessage);
      socketService.off('board_state', handleSkillChatMessage);
    };
  }, []); // 空依赖，通过 ref 和 store 访问最新状态

  /**
   * 清理 treeData 中无效的临时节点
   * 保留：数字类型 ID、数字字符串 ID（如 "2832"）
   * 移除：临时 ID（如 "pending-xxx"、"user-xxx"）
   */
  const pruneInvalidTreeNodes = useCallback((nodes: any[]): any[] => {
    return nodes
      .filter((node: any) => {
        // 🔥 修复：接受数字类型或数字字符串类型的 ID
        // 数字类型直接通过
        if (typeof node.id === 'number') return true;
        if (typeof node.backendId === 'number') return true;
        // 数字字符串也通过（后端可能返回字符串类型的数字 ID）
        if (
          typeof node.backendId === 'string' &&
          !isNaN(Number(node.backendId))
        )
          return true;
        // 检查 node.id 是否是有效的数字字符串（排除 pending-xxx、user-xxx 等临时 ID）
        if (typeof node.id === 'string') {
          const id = node.id;
          // 排除临时 ID
          if (
            id.startsWith('pending-') ||
            id.startsWith('user-') ||
            id.startsWith('temp-')
          ) {
            return false;
          }
          // 数字字符串通过
          if (!isNaN(Number(id))) return true;
        }
        return false;
      })
      .map((node: any) => {
        const newNode = { ...node };
        if (Array.isArray(node.children) && node.children.length > 0) {
          newNode.children = pruneInvalidTreeNodes(node.children);
        }
        return newNode;
      });
  }, []);

  /**
   * 创建新对话
   */
  const handleNewConversation = useCallback(() => {
    // 清除当前对话选择，让用户看到欢迎页面
    setCurrentConversationId(undefined);
    currentConversationIdRef.current = undefined;
    currentConversationIdRefForCallback.current = undefined;

    // 清除 newChatAgent，让用户重新选择
    setNewChatAgent(null);

    // 🔥 直接关闭 Tasks Status 面板（不依赖 useEffect 的时序）
    useSkillTaskStore.getState().setDashboardOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCurrentConversationId, setNewChatAgent]);

  /**
   * 发送消息
   */
  const handleSendMessage = useCallback(
    async (
      content: string,
      isRetry?: boolean,
      options?: Record<string, any>
    ) => {
      if (!content.trim() && !isRetry) return;

      // 任务队列逻辑
      const {
        canSendNextTask,
        addTaskToQueue,
        getLastAssistantMessageId,
        taskQueue,
      } = useUIStore.getState();

      // 待确认消息ID
      const pendingId = options?.pendingId || createPendingId();
      const targetConversationId =
        options?.conversationId || currentConversationId || '';

      // 检查等待队列是否已满
      // 🔥 修复：只计算目标对话的待处理任务数，不阻塞其他对话
      // 新对话（targetConversationId 为空）不受其他 skill 对话任务的影响
      const tasksInTargetConv = targetConversationId
        ? taskQueue.filter(
            t =>
              t.conversationId === targetConversationId &&
              (t.status === 'pending' ||
                t.status === 'sent' ||
                t.status === 'initialized')
          ).length
        : 0;
      if (tasksInTargetConv >= 10 && !isRetry) {
        console.warn('⚠️ 当前对话的等待任务队列已满（10个），无法发送新消息');
        return;
      }

      // 判断是否是群聊
      const isGroupChatEarly = window.groupChatIds?.has(targetConversationId);

      // 检查是否可以立即发送
      // 🔥 修复：只有当活跃任务属于同一对话时才阻止发送
      // 不同对话的任务（如 skill 对话 vs 普通 new chat）不应互相阻塞
      const hasActiveTaskInSameConversation = taskQueue.some(
        t =>
          t.conversationId === targetConversationId &&
          (t.status === 'sent' ||
            t.status === 'initialized' ||
            t.status === 'streaming')
      );
      const shouldQueue = !canSendNextTask() && hasActiveTaskInSameConversation;
      if (shouldQueue && !isRetry && !isGroupChatEarly) {
        const added = addTaskToQueue({
          id: pendingId,
          type: 'chat', // 🔥 新增：标记为 chat 任务
          content,
          conversationId: targetConversationId,
          projectId: options?.projectId || projectId || undefined,
          agentId: selectedAgent?.id || null,
          options: {
            ...options,
            fileIds: options?.fileIds,
          },
        });

        if (added) {
          console.log('📝 任务已加入等待队列', {
            taskId: pendingId,
            content: content.slice(0, 50),
            queueLength: taskQueue.length + 1,
          });

          // 🔥 新增：直接在对话树中添加节点，显示 'waiting' 状态
          // 不调用 sendMessage，避免复杂的渲染逻辑
          const pendingTasksInConversation = taskQueue.filter(
            t =>
              t.conversationId === targetConversationId &&
              (t.status === 'pending' ||
                t.status === 'sent' ||
                t.status === 'initialized' ||
                t.status === 'streaming')
          );

          // 获取前端的 previousMessageId（链接到前一个等待中的任务）
          let frontendPrevId: string | number | undefined;
          if (pendingTasksInConversation.length > 0) {
            const lastPendingTask =
              pendingTasksInConversation[pendingTasksInConversation.length - 1];
            frontendPrevId = lastPendingTask.id; // 使用前一个任务的 pendingId
          } else {
            // 如果没有等待中的任务，查找最后一个 assistant 消息
            // 🔥 修复：将 null 转换为 undefined 以匹配类型
            frontendPrevId =
              getLastAssistantMessageId(targetConversationId) ?? undefined;
          }

          // 直接修改 conversation 状态，添加 waiting 状态的节点
          setConversationsRef.current(prev =>
            prev.map(conv => {
              if (conv.id !== targetConversationId) return conv;

              // 构建用户消息的 blocks
              const userBlocks = [];
              if (options?.fileIds && Array.isArray(options.fileIds)) {
                for (const fileId of options.fileIds) {
                  userBlocks.push({
                    type: 'file',
                    content: fileId,
                  });
                }
              }
              userBlocks.push({
                type: 'text',
                content: content,
              });

              // 创建 user 节点
              // 🔥 类型断言：SSE 流式渲染时使用字符串 ID，后端返回后会更新为数字 ID
              const userNodeId = `user-${pendingId}`;
              const userNode = {
                id: userNodeId as any, // pending ID 是字符串，后续会更新为数字
                role: 'user' as const,
                contents: userBlocks.map(block => ({
                  type: block.type,
                  content: block.content,
                  createdAt: new Date().toISOString(),
                })),
                isStreaming: false,
                messageStatus: 'waiting', // 队列中等待
                previousMessageId: frontendPrevId as any, // 🔥 类型断言：可能是字符串 pending ID
                backendId: undefined,
                children: [] as any[],
              };

              // 创建 assistant 节点
              const assistantNode = {
                id: pendingId as any, // pending ID 是字符串，后续会更新为数字
                role: 'assistant' as const,
                contents: [],
                isStreaming: true,
                messageStatus: 'waiting', // 队列中等待
                previousMessageId: userNodeId,
                backendId: undefined,
                client: selectedAgent
                  ? {
                      id: selectedAgent.id,
                      name: selectedAgent.name,
                      logo: selectedAgent.logo,
                    }
                  : undefined,
                children: [] as any[],
              };

              // 将 assistant 作为 user 的子节点
              userNode.children = [assistantNode];

              // 添加到 treeData
              let updatedTreeData = conv.treeData || [];

              if (frontendPrevId) {
                // 查找父节点并添加
                const addChildToParent = (nodes: any[]): any[] => {
                  return nodes.map(node => {
                    if (
                      String(node.id) === String(frontendPrevId) ||
                      String(node.backendId) === String(frontendPrevId)
                    ) {
                      return {
                        ...node,
                        children: [...(node.children || []), userNode],
                      };
                    }
                    if (node.children && node.children.length > 0) {
                      return {
                        ...node,
                        children: addChildToParent(node.children),
                      };
                    }
                    return node;
                  });
                };
                updatedTreeData = addChildToParent(updatedTreeData);
              } else {
                // 没有父节点，添加为根节点
                updatedTreeData = [...updatedTreeData, userNode];
              }

              return {
                ...conv,
                treeData: updatedTreeData,
              };
            })
          );
        }
        return;
      }

      // 获取 previousMessageId（只查找同一会话的任务，避免跨会话串台）
      const queuedPreviousMessageId = isRetry
        ? (options?.lastMessageId ?? null)
        : getLastAssistantMessageId(targetConversationId);

      // 🔥 新增：检查是否有同一会话的未完成任务
      // 如果有，新消息应该链接到那个任务的 pendingId，而不是共同的父节点
      // 这样可以避免连续发送消息时形成版本分支
      let frontendPreviousMessageId: string | number | undefined;
      const pendingTasksInConversation = taskQueue.filter(
        t =>
          t.conversationId === targetConversationId &&
          (t.status === 'pending' ||
            t.status === 'sent' ||
            t.status === 'initialized' ||
            t.status === 'streaming')
      );
      if (pendingTasksInConversation.length > 0) {
        // 使用最后一个未完成任务的 pendingId 作为前端的 previousMessageId
        const lastPendingTask =
          pendingTasksInConversation[pendingTasksInConversation.length - 1];

        // 🔥 修复：检查 latestMessageInit 是否比未完成任务更新
        // 如果 latestMessageInit.assistantMessageId > lastPendingTask.assistantMessageId，
        // 说明有 cancel 操作发生，新消息应该链接到 cancel 节点而不是未完成任务
        const latestInit = useUIStore
          .getState()
          .getLatestMessageInit(targetConversationId);
        const latestAssistantId = latestInit?.assistantMessageId;
        const pendingAssistantId = lastPendingTask.assistantMessageId;

        // 只有当未完成任务的 assistantMessageId >= latestMessageInit.assistantMessageId 时才使用
        // 或者未完成任务还没有 assistantMessageId（还没收到 message_init）
        const shouldUsePendingTask =
          !latestAssistantId ||
          !pendingAssistantId ||
          pendingAssistantId >= latestAssistantId;

        if (shouldUsePendingTask) {
          frontendPreviousMessageId = lastPendingTask.id; // pendingId
        }
      }

      // 清理 treeData 中的临时节点
      // 🔥 关键修复：skill 会话跳过 treeData 清理
      // skill 模式下消息通过 WebSocket 添加到 conv.messages，不会同步到 treeData
      // 如果在这里用 treeData 重建 messages，会丢失 WS 添加的消息（导致第三个问题覆盖第二个问题）
      const isSkillConv = useUIStore
        .getState()
        .isSkillConversation(
          options?.conversationId || currentConversationId || ''
        );
      if (!isRetry && !isGroupChatEarly && !isSkillConv) {
        setConversationsRef.current(prev =>
          prev.map(conv => {
            if (conv.id !== (options?.conversationId || currentConversationId))
              return conv;
            if (!conv.treeData || conv.treeData.length === 0) return conv;

            const pruned = pruneInvalidTreeNodes(conv.treeData);
            // 🔥 修复：保留原有的 versionPath，而不是创建空对象
            // 这样可以避免发送失败后重试时，previousMessageId 指向错误的分支
            const newVersionPath = { ...(conv.versionPath || {}) };
            const newMessages = rebuildMessagesWithVersionPath(
              pruned,
              conv.conversationId,
              newVersionPath
            );

            return {
              ...conv,
              treeData: pruned,
              versionPath: newVersionPath,
              messages: newMessages,
            };
          })
        );
      }

      let conversationId = options?.conversationId || currentConversationId;

      // 创建新对话
      if (!conversationId) {
        conversationId = 'temp-' + generateId();

        if (newChatAgent) {
          setConversationAgent(conversationId, newChatAgent);
        }

        const selectedProjectId = options?.projectId || projectId || undefined;
        // 🔥 Skill 模式：新对话创建时直接包含 user + assistant 消息
        // 避免两次 state update 导致 loading 出现延迟
        const isSkillNewConv = !!options?.isSkillTask;
        let initialMessages: Message[] = [];
        if (isSkillNewConv) {
          const userBlocks: MessageBlock[] = [];
          if (options?.fileIds && Array.isArray(options.fileIds)) {
            for (const fid of options.fileIds) {
              userBlocks.push({
                type: 'file',
                content: fid,
                id: fid,
                fileId: fid,
              });
            }
          }
          userBlocks.push({ type: 'text', content });
          initialMessages = [
            {
              id: `user-${pendingId}`,
              role: 'user',
              choices: [{ blocks: userBlocks, version: 1 }],
              timestamp: new Date(),
              isStreaming: false,
            },
            {
              id: `assistant-${pendingId}`,
              role: 'assistant',
              choices: [{ blocks: [], version: 1 }],
              timestamp: new Date(),
              isStreaming: true,
              client: selectedAgent
                ? {
                    id: selectedAgent.id,
                    name: selectedAgent.name,
                    logo: selectedAgent.logo,
                  }
                : undefined,
            },
          ];
        }

        const newConversation = {
          id: conversationId,
          conversationId,
          title: truncateText(content, 50),
          messages: initialMessages, // 🔥 skill 模式：直接包含消息，避免延迟
          treeData: [],
          versionPath: {},
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          projectId: selectedProjectId,
          pendingFrontendTempIds: [pendingId], // 🔥 重构：使用 pendingId
          _isSkillNewConv: isSkillNewConv, // 🔥 标记：后续 skill 模式块跳过重复添加消息
        };

        setConversationsRef.current(prev => {
          const newState = [newConversation, ...prev];
          conversationsRef.current = newState;
          return newState;
        });

        if (selectedProjectId) {
          setSidebarProjectConversationsCacheRef.current(prev => ({
            ...prev,
            [selectedProjectId]: [
              newConversation,
              ...(prev[selectedProjectId] || []),
            ],
          }));
        }

        // 🔥 修复：如果是 skill 新对话，在切换 UI 之前就注册为 skill 对话
        // 这样 setCurrentConversationId 触发渲染时，isCurrentSkillConversation 已经是 true
        // useMessagesArray=true → 走 MessageBubble 路径 → 立即渲染预填充的 messages
        if (isSkillNewConv) {
          useUIStore.getState().addSkillConversation(conversationId);
        }

        setCurrentConversationId(conversationId);
        currentConversationIdRef.current = conversationId;
        currentConversationIdRefForCallback.current = conversationId;
      }

      // 添加任务到队列
      if (!isRetry) {
        addTaskToQueue({
          id: pendingId, // 🔥 重构：使用 pendingId
          type: 'chat', // 🔥 新增：标记为 chat 任务
          content,
          conversationId,
          projectId: options?.projectId || projectId || undefined,
          agentId: selectedAgent?.id || null,
          options: { ...options, fileIds: options?.fileIds },
        });
        // 🔥 新增：立即更新状态为 'sent'，防止后续任务也进入直接发送路径
        // canSendNextTask() 只检查 'sent'/'initialized'/'streaming' 状态
        // 如果不立即更新，后续快速发送的消息也会走直接发送路径，导致 previousMessageId 相同
        useUIStore.getState().updateTaskStatus(pendingId, 'sent');
      }

      // 更新 loading 状态
      if (!isGroupChatEarly) {
        updateConversationLoadingStateRef.current(conversationId, true);
      }

      // 计算 lastMessageId
      let lastMessageId: number | undefined;

      // 🔥 修复：新会话（temp-xxx）不应该有 lastMessageId
      // 新会话的第一条消息的 previousMessageId 应该是 null
      // 这避免了新会话错误地使用旧会话的 lastMessageId 导致消息串台
      const isNewConversation = conversationId?.startsWith('temp-');
      if (isNewConversation) {
        lastMessageId = undefined;
        console.log('📍 新会话，lastMessageId 设为 undefined');
      } else if (queuedPreviousMessageId) {
        lastMessageId = queuedPreviousMessageId;
      } else if (options?.lastMessageId) {
        lastMessageId = options.lastMessageId;
      } else {
        const conv = conversationsRef.current.find(
          c => c.id === conversationId
        );
        // 🔥 优先从 treeData 中查找，因为 treeData 包含完整的树结构和所有类型的节点
        // messages 数组可能不包含 cancel 节点，或者 cancel 节点的 role 是 'system' 不会被匹配
        if (conv?.treeData?.length) {
          // 🔥 修复：按照 versionPath（用户当前激活的版本路径）来遍历，而不是每次都取最后一个节点
          // versionPath: Record<number, number> = { depth: selectedIndex }
          const getDeepestParentIdByVersionPath = (
            nodes: any[],
            versionPath: Record<number, number>,
            depth: number = 0
          ): number | string | null => {
            if (!nodes || nodes.length === 0) return null;

            // 🔥 根据 versionPath 选择当前深度的节点，默认选第一个（索引 0）
            const selectedIndex = versionPath[depth] ?? 0;
            const selectedNode = nodes[selectedIndex] || nodes[0];

            // 递归查找最深的节点
            if (selectedNode.children && selectedNode.children.length > 0) {
              const deeperId = getDeepestParentIdByVersionPath(
                selectedNode.children,
                versionPath,
                depth + 1
              );
              if (deeperId) return deeperId;
            }

            // 🔥 如果是 assistant 节点或 cancel 节点，返回其 backendId 或 id
            // cancel 节点也可以作为后续消息的 parent
            if (
              selectedNode.role === 'assistant' ||
              selectedNode.type === 'cancel'
            ) {
              return selectedNode.backendId || selectedNode.id;
            }

            return null;
          };

          const versionPath = conv.versionPath || {};
          const deepestId = getDeepestParentIdByVersionPath(
            conv.treeData,
            versionPath
          );
          if (deepestId && typeof deepestId === 'number') {
            lastMessageId = deepestId;
          } else if (deepestId && !isNaN(Number(deepestId))) {
            lastMessageId = Number(deepestId);
          }
          console.log('📍 从 treeData 获取 lastMessageId:', {
            deepestId,
            lastMessageId,
          });
        }
        // 🔥 如果从 treeData 中找不到，再从 messages 中查找（fallback）
        if (!lastMessageId && conv?.messages?.length) {
          for (let i = conv.messages.length - 1; i >= 0; i--) {
            const msg = conv.messages[i];
            if (msg.role === 'assistant' && msg.backendId) {
              lastMessageId = msg.backendId;
              break;
            }
          }
        }
      }

      const finalProjectId = options?.projectId || projectId || '';
      const clientId =
        options?.clientId !== undefined
          ? options.clientId
          : selectedAgent?.id || null;
      const { ...restOptions } = options || {};

      // 🔥 新增：将 frontendPreviousMessageId 添加到 options 中
      // 当有未完成的任务时，使用其 pendingId 作为前端树结构的 previousMessageId
      const finalOptions = {
        ...restOptions,
        frontendPreviousMessageId: frontendPreviousMessageId || lastMessageId,
      };

      try {
        const isGroupChat = window.groupChatIds?.has(conversationId);

        // 🔥 Skill 模式判断：当前消息有 isSkillTask 标记 OR 该会话已被标记为 skill 会话
        // 一旦会话进入 skill 模式，后续所有消息（不管有没有 [skill:TFL] 前缀）都自动走 skill 模式
        const isSkillModeHere =
          !!restOptions?.isSkillTask ||
          useUIStore.getState().isSkillConversation(conversationId);

        if (isSkillModeHere) {
          console.log(
            '🎯 Skill mode active, sending via SSE + WebSocket listening...',
            {
              isSkillTask: !!restOptions?.isSkillTask,
              isSkillConversation: useUIStore
                .getState()
                .isSkillConversation(conversationId),
            }
          );

          // 🔥 标记该会话为 skill 会话（首次进入时注册，已注册不重复）
          if (!useUIStore.getState().isSkillConversation(conversationId)) {
            useUIStore.getState().addSkillConversation(conversationId);
          }

          // 🔥 主动加入 WebSocket 房间（绕过 React 异步状态更新的时序问题）
          if (socketService.isSocketConnected()) {
            console.log(
              '🔔 Skill mode: joining conversation room before send:',
              conversationId
            );
            socketService.emit('join_conversation_room', conversationId);
          }

          // 🔥 board 数据在 WS message_init 到达后（获得真实 conversationId）再调用 fetchBoard
          // 不在这里调用，因为此时 conversationId 可能还是 temp ID

          // 🔥 将 sid 注入 finalOptions，让 EventSourceClient 把它加到 formData 顶层
          // 后端通过 sid 知道应该将流式响应发到哪个 WebSocket 连接
          const skillSid = socketService.getSocketId();
          if (skillSid) {
            (finalOptions as any).sid = skillSid;
          }

          // 🔥 Skill 模式：手动添加 user + assistant 消息到 conv.messages
          // 如果是新对话（已在创建时预填充消息），跳过重复添加
          {
            const conv = conversationsRef.current.find(
              c =>
                c.id === conversationId || c.conversationId === conversationId
            );
            // 🔥 修复：检查这个特定 pendingId 的消息是否已存在（新对话创建时预填充的）
            // 而不是检查对话是否有任何消息（否则第二条消息永远不会被添加）
            const thisMessageAlreadyExists = conv?.messages?.some(
              m =>
                String(m.id) === `user-${pendingId}` ||
                String(m.id) === `assistant-${pendingId}`
            );

            if (!thisMessageAlreadyExists) {
              // 这个 pendingId 的消息不存在，需要添加
              const userBlocks: MessageBlock[] = [];
              if (restOptions?.fileIds && Array.isArray(restOptions.fileIds)) {
                for (const fid of restOptions.fileIds) {
                  userBlocks.push({
                    type: 'file',
                    content: fid,
                    id: fid,
                    fileId: fid,
                  });
                }
              }
              userBlocks.push({ type: 'text', content });
              const skillUserMsg: Message = {
                id: `user-${pendingId}`,
                role: 'user',
                choices: [{ blocks: userBlocks, version: 1 }],
                timestamp: new Date(),
                isStreaming: false,
              };
              const skillAssistantMsg: Message = {
                id: `assistant-${pendingId}`,
                role: 'assistant',
                choices: [{ blocks: [], version: 1 }],
                timestamp: new Date(),
                isStreaming: true,
                client: selectedAgent
                  ? {
                      id: selectedAgent.id,
                      name: selectedAgent.name,
                      logo: selectedAgent.logo,
                    }
                  : undefined,
              };
              setConversationsRef.current(prev =>
                prev.map(conv => {
                  if (
                    conv.id !== conversationId &&
                    conv.conversationId !== conversationId
                  )
                    return conv;
                  return {
                    ...conv,
                    messages: [
                      ...(conv.messages || []),
                      skillUserMsg,
                      skillAssistantMsg,
                    ],
                  };
                })
              );
              console.log(
                '🎯 Skill mode: user message + loading assistant added to conv.messages'
              );
            } else {
              console.log(
                '🎯 Skill mode: messages already pre-populated in new conversation, skipping duplicate add'
              );
            }
          }

          // 🔥 不提前 return，继续走到底部的 sendMessageRef.current()（SSE 路径）
          // SSE 负责发送消息并接收 messageId
          // WebSocket 负责接收 message_init 和流式内容（通过 useEffect 中显式注册的 socketService.on）
          // eventsource.ts 中的 skillTaskPendingIds/isSkillConversation 检查会在 SSE 流提前结束时静默处理
          console.log(
            '🎯 Skill mode setup complete, falling through to SSE sendMessage...'
          );
        }

        if (isGroupChat) {
          const hasAtAgent = /@Agent\b/gi.test(content);

          await sendGroupMessage({
            conversationId,
            message: content,
            clientId: clientId,
            fileIds: restOptions?.fileIds || [],
            projectId: finalProjectId,
            apiType: restOptions?.apiType,
          });

          if (!hasAtAgent) {
            updateConversationLoadingStateRef.current(conversationId, false);
            // 🔥 修复：群聊消息（没有 @Agent）发送成功后，手动移除任务
            // 普通消息通过 SSE 的 message_end 事件来移除任务
            // 但群聊消息（没有 @Agent）不会触发 SSE 流，所以需要手动移除
            useUIStore.getState().removeTask(pendingId);
          }
        } else {
          await sendMessageRef.current(
            content,
            conversationId,
            pendingId, // 🔥 重构：使用 pendingId
            finalProjectId,
            clientId,
            finalOptions, // 🔥 使用 finalOptions，包含 frontendPreviousMessageId
            lastMessageId
          );
        }
      } catch (error) {
        console.error('❌ Failed to send message:', error);
        updateConversationLoadingStateRef.current(conversationId, false);
      }
    },
    [
      currentConversationId,
      projectId,
      selectedAgent,
      newChatAgent,
      setConversationAgent,
      setCurrentConversationId,
      pruneInvalidTreeNodes,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ]
  );

  /**
   * 取消消息
   * @param messageId 消息 ID (后端 ID 或 pendingId)
   * @param previousMessageId 前一条消息的 ID
   * @param messageStatus 消息状态：waiting/pending/ongoing
   * @param conversationId 对话 ID
   */
  const handleCancelMessage = useCallback(
    async (
      messageId: number | string | undefined,
      previousMessageIdParam: number | string | undefined, // 🔥 上一条消息的 assistantMessageId
      messageStatus: MessageStatus | undefined,
      conversationId: string
    ): Promise<{ success: boolean }> => {
      // 🔥 参数校验：缺少必需参数时提前返回
      if (!messageId || !messageStatus) {
        console.warn('⚠️ handleCancelMessage: 缺少必需参数', {
          messageId,
          messageStatus,
        });
        return { success: false };
      }
      // 1. waiting 状态：直接从队列和 UI 中移除
      if (messageStatus === 'waiting') {
        // 从队列中移除
        const { removeTask, removeActiveStreamingMessage } =
          useUIStore.getState();
        removeTask(String(messageId));
        // 🔥 从活跃流式消息 Map 中移除
        removeActiveStreamingMessage(conversationId, String(messageId));

        // 从 treeData 中移除该节点及其父节点（user 节点）
        setConversationsRef.current(prev =>
          prev.map(conv => {
            if (conv.id !== conversationId || !conv.treeData) return conv;

            // 递归删除包含该 messageId 的节点链（user + assistant）
            const removeNodeChain = (nodes: any[]): any[] => {
              return nodes
                .filter(node => {
                  // 如果是 user 节点且其子节点包含目标 assistant 节点，移除整个链
                  if (node.role === 'user' && node.children?.length > 0) {
                    const hasTargetChild = node.children.some(
                      (child: any) =>
                        String(child.id) === String(messageId) ||
                        String(child.backendId) === String(messageId)
                    );
                    if (hasTargetChild) return false;
                  }
                  // 如果直接是目标节点，移除
                  if (
                    String(node.id) === String(messageId) ||
                    String(node.backendId) === String(messageId)
                  ) {
                    return false;
                  }
                  return true;
                })
                .map(node => {
                  if (node.children?.length > 0) {
                    return {
                      ...node,
                      children: removeNodeChain(node.children),
                    };
                  }
                  return node;
                });
            };

            return {
              ...conv,
              treeData: removeNodeChain(conv.treeData),
            };
          })
        );

        console.log('🗑️ 已从队列和 UI 中移除 waiting 状态的消息', {
          messageId,
        });
        return { success: true };
      }

      // 2. pending/ongoing 状态：根据是否有 backendId 区分处理
      if (messageStatus === 'pending' || messageStatus === 'ongoing') {
        // 🔥 直接使用传入的参数（来自 latestMessageInitMap）
        // TreeMessageRenderer 已经从 latestMessageInitMap 获取了正确的 userMessageId 和 assistantMessageId
        let userMessageId: number | undefined =
          typeof messageId === 'number' ? messageId : undefined;
        let realPreviousMessageId: number | undefined =
          typeof previousMessageIdParam === 'number'
            ? previousMessageIdParam
            : undefined;

        console.log('🔍 handleCancelMessage: 使用传入的参数', {
          userMessageId,
          assistantMessageId: realPreviousMessageId,
        });

        const conversation = conversationsRef.current.find(
          c => c.id === conversationId
        );

        if (conversation?.treeData) {
          // 🔥 递归查找 user 节点的 backendId 和 previousMessageId
          const findUserNodeInfo = (
            nodes: any[],
            targetId: string | number
          ):
            | { backendId?: number; previousMessageId?: number | string }
            | undefined => {
            for (const node of nodes) {
              // 🔥 只匹配 user 节点
              if (
                node.role === 'user' &&
                (String(node.id) === String(targetId) ||
                  String(node.backendId) === String(targetId))
              ) {
                return {
                  backendId:
                    typeof node.backendId === 'number'
                      ? node.backendId
                      : undefined,
                  previousMessageId: node.previousMessageId,
                };
              }
              if (node.children?.length > 0) {
                const result = findUserNodeInfo(node.children, targetId);
                if (result) return result;
              }
            }
            return undefined;
          };

          // 🔥 只查找 userMessageId（如果传入的是字符串 ID，需要转换为数字 backendId）
          // 🔥 realPreviousMessageId 直接使用传入的参数，不能从 treeData 查找！
          const userNodeInfo = findUserNodeInfo(
            conversation.treeData,
            messageId as string | number
          );

          if (userNodeInfo && userNodeInfo.backendId !== undefined) {
            userMessageId = userNodeInfo.backendId;
          }

          // 🔥 不再从 treeData 查找 realPreviousMessageId
          // realPreviousMessageId 必须使用传入的参数（来自 latestMessageInitMap 的 assistantMessageId）
          // 不能从父节点获取，因为父节点的 backendId 是"上一条消息"的 ID，不是最新 message_init 的 assistantMessageId
        }

        // 🔥 如果没有 backendId，说明还没收到 message_init，加入取消任务队列
        if (userMessageId === undefined) {
          // 🔥 从 user 节点 ID 中提取 pendingId（去掉 "user-" 前缀）
          const userNodeId = String(messageId);
          const pendingId = userNodeId.startsWith('user-')
            ? userNodeId.slice(5) // 去掉 "user-" 前缀
            : userNodeId;

          // 🔥 添加 cancel 任务到队列最前面（统一任务队列管理）
          const { insertCancelTaskToFront, removeActiveStreamingMessage } =
            useUIStore.getState();
          insertCancelTaskToFront({
            conversationId,
            targetPendingId: pendingId, // 用于匹配还没收到 message_init 的消息
          });
          // 🔥 从活跃流式消息 Map 中移除
          removeActiveStreamingMessage(conversationId, pendingId);

          console.log(
            '📝 消息尚未收到 message_init，已插入 cancel 任务到队列最前面',
            {
              pendingId,
              conversationId,
            }
          );

          // 更新 UI 中的消息状态为 'canceled'
          setConversationsRef.current(prev =>
            prev.map(conv => {
              if (conv.id !== conversationId || !conv.treeData) return conv;

              const updateCanceledStatus = (nodes: any[]): any[] => {
                return nodes.map(node => {
                  if (
                    String(node.id) === String(messageId) ||
                    String(node.backendId) === String(messageId)
                  ) {
                    return {
                      ...node,
                      messageStatus: 'canceled' as MessageStatus,
                      isStreaming: false,
                    };
                  }
                  if (node.children?.length > 0) {
                    return {
                      ...node,
                      children: updateCanceledStatus(node.children),
                    };
                  }
                  return node;
                });
              };

              return {
                ...conv,
                treeData: updateCanceledStatus(conv.treeData),
              };
            })
          );

          return { success: true };
        }

        // 🔥 有 backendId，插入 cancel 任务到队列最前面（统一任务队列管理）
        // 不再直接调用 API，而是通过队列处理
        const {
          insertCancelTaskToFront,
          canSendNextTask,
          getNextPendingTask,
          removeActiveStreamingMessage,
        } = useUIStore.getState();
        insertCancelTaskToFront({
          conversationId,
          targetUserMessageId: userMessageId,
          previousMessageId: realPreviousMessageId,
        });
        // 🔥 从活跃流式消息 Map 中移除
        const cancelPendingId = String(messageId).startsWith('user-')
          ? String(messageId).slice(5)
          : String(messageId);
        removeActiveStreamingMessage(conversationId, cancelPendingId);

        console.log(
          '📝 消息已收到 message_init，已插入 cancel 任务到队列最前面',
          {
            userMessageId,
            previousMessageId: realPreviousMessageId,
            conversationId,
          }
        );

        // 🔥 关键修复：如果队列之前为空或当前没有正在处理的任务，立即触发执行
        // 否则 cancel 任务会永远不被执行
        if (canSendNextTask()) {
          const nextTask = getNextPendingTask();
          if (nextTask) {
            console.log('📍 队列空闲，立即触发 cancel 任务执行', {
              taskId: nextTask.id,
              taskType: nextTask.type,
            });
            window.dispatchEvent(
              new CustomEvent('sendNextPendingTask', {
                detail: {
                  task: nextTask,
                  previousMessageId: realPreviousMessageId,
                },
              })
            );
          }
        }

        // 更新 UI 中的消息状态为 'canceled'（立即反馈给用户）
        setConversationsRef.current(prev =>
          prev.map(conv => {
            if (conv.id !== conversationId || !conv.treeData) return conv;

            const updateCanceledStatus = (nodes: any[]): any[] => {
              return nodes.map(node => {
                if (
                  String(node.id) === String(messageId) ||
                  String(node.backendId) === String(messageId)
                ) {
                  return {
                    ...node,
                    messageStatus: 'canceled' as MessageStatus,
                    isStreaming: false,
                  };
                }
                if (node.children?.length > 0) {
                  return {
                    ...node,
                    children: updateCanceledStatus(node.children),
                  };
                }
                return node;
              });
            };

            return {
              ...conv,
              treeData: updateCanceledStatus(conv.treeData),
            };
          })
        );

        return { success: true };
      }

      return { success: false };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return {
    handleNewConversation,
    handleSendMessage,
    handleCancelMessage,
    pruneInvalidTreeNodes,
  };
}

export default useMessageOperations;
